import { z } from 'zod';
import { uuidSchema } from './common.schema';

export const MILK_BATCH_STATUS = ['Unapproved', 'Approved', 'Failed', 'Retest', 'Rejected'] as const;
export type MilkBatchStatus = (typeof MILK_BATCH_STATUS)[number];
export const MilkBatchStatusEnum = z.enum(MILK_BATCH_STATUS);

export const MilkBatchBottleSchema = z.object({
  volume_ml: z.number().positive(),
  quantity: z.number().int().positive(),
  is_quality_sample: z.boolean(),
});

export type MilkBatchBottle = z.infer<typeof MilkBatchBottleSchema>;

export const MilkBatchInventoryItemSchema = z.object({
  inventory_id: uuidSchema,
  container_barcode: z.string(),
  is_quality_sample: z.boolean(),
  initial_volume_ml: z.number().nullable(),
  remaining_volume_ml: z.number().nullable(),
  quantity: z.number().nullable(),
  status: z.string(),
  expiry_at: z.string().nullable(),
  storage_location_id: uuidSchema.nullable(),
  storage_location_name: z.string().nullable(),
});
export type MilkBatchInventoryItem = z.infer<typeof MilkBatchInventoryItemSchema>;

export const CreateMilkBatchSchema = z.object({
  pool_id: uuidSchema,
  hospital_id: uuidSchema,
  location_name: z.string(),
  expiry_at: z.string().datetime().optional(),
  is_pool_empty: z.boolean(),
  bottles: z.array(MilkBatchBottleSchema).min(1),
  /** UUID of the user creating this batch (also recorded as `aliquoted_by` on each generated bottle). */
  created_by: uuidSchema.optional(),
});

export type CreateMilkBatchRequest = z.infer<typeof CreateMilkBatchSchema>;

export const MilkBatchResponseSchema = z.object({
  batch_id: uuidSchema,
  batch_number: z.string(),
  pool_id: uuidSchema.nullable(),
  hospital_id: uuidSchema,
  source_type: z.string(),
  provider_name: z.string().optional(),
  total_volume_ml: z.number().nullable(),
  location_name: z.string().nullable(),
  status: z.string(),
  expiry_at: z.string().nullable(),
  created_at: z.string(),
  updated_at: z.string(),
  approval_location: z.string().nullable(),
  upload_verification_document: z.string().nullable(),
  is_certify: z.boolean(),
  is_pool_empty: z.boolean(),
  inventory_items: z.array(MilkBatchInventoryItemSchema),
});

export type MilkBatchResponse = z.infer<typeof MilkBatchResponseSchema>;

export const MilkInventoryListQuerySchema = z.object({
  page:         z.coerce.number().int().positive().default(1),
  limit:        z.coerce.number().int().positive().max(100).default(25),
  status:       z.string().optional(),
  batch_number: z.string().optional(),
});

export type MilkInventoryListQuery = z.infer<typeof MilkInventoryListQuerySchema>;

export const MilkBatchListItemSchema = z.object({
  batch_id:              uuidSchema,
  batch_number:          z.string(),
  pool_id:               uuidSchema.nullable(),
  hospital_id:           uuidSchema,
  source_type:           z.string(),
  location_name:         z.string().nullable(),
  volume_ml:             z.number().nullable(),
  remaining_volume_ml:   z.number().nullable(),
  status:                z.string(),
  expiry_at:             z.string().nullable(),
  approval_location:            z.string().nullable(),
  upload_verification_document: z.string().nullable(),
  is_certify:                   z.boolean(),
  is_pool_empty:                z.boolean(),
  created_at:            z.string(),
});

export type MilkBatchListItem = z.infer<typeof MilkBatchListItemSchema>;

export const MilkInventoryListItemSchema = z.object({
  inventory_id:       uuidSchema,
  batch_id:           uuidSchema,
  batch_number:       z.string(),
  location_name:      z.string().nullable(),
  volume_ml:          z.number().nullable(),
  status:             z.string(),
  expiry_at:          z.string().nullable(),
});

export type MilkInventoryListItem = z.infer<typeof MilkInventoryListItemSchema>;

export const MilkInventoryDetailSchema = z.object({
  inventory_id:        uuidSchema,
  batch_id:            uuidSchema,
  batch_number:        z.string(),
  hospital_id:         uuidSchema,
  hospital_name:       z.string().nullable(),
  container_barcode:   z.string(),
  is_quality_sample:   z.boolean(),
  initial_volume_ml:   z.number().nullable(),
  remaining_volume_ml: z.number().nullable(),
  quantity:            z.number().nullable(),
  storage_location_id: uuidSchema.nullable(),
  location_name:       z.string().nullable(),
  location_type:       z.string().nullable(),
  location_barcode:    z.string().nullable(),
  status:              z.string(),
  expiry_at:           z.string().nullable(),
  stored_at:           z.string().nullable(),
  batch: z.object({
    batch_number:  z.string(),
    source_type:   z.string(),
    status:        z.string(),
    volume_ml:     z.number().nullable(),
    expiry_at:     z.string().nullable(),
    received_at:   z.string(),
    notes:         z.string().nullable(),
    provider_name: z.string().nullable(),
  }),
  aliquoted_by_id:   uuidSchema.nullable(),
  aliquoted_by_name: z.string().nullable(),
  created_at:        z.string(),
  updated_at:        z.string(),
});

export type MilkInventoryDetail = z.infer<typeof MilkInventoryDetailSchema>;

export const UpdateMilkInventorySchema = z.object({
  status:         MilkBatchStatusEnum.optional(),
  pool_id:        uuidSchema.optional().nullable(),
  location_name:  z.string().optional().nullable(),
  expiry_at:      z.string().datetime().optional().nullable(),
  notes:          z.string().optional(),
  approval_location:            z.string().optional().nullable(),
  upload_verification_document: z.string().optional().nullable(),
  is_certify:                   z.boolean().optional(),
  is_pool_empty:                z.boolean().optional(),
  bottles:        z.array(MilkBatchBottleSchema).min(1).optional(),
});

export type UpdateMilkInventoryRequest = z.infer<typeof UpdateMilkInventorySchema>;

